import React, { forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Tooltip, AttachmentIcon } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { AttachmentMenuPicker } from "./index.jsx";
import { keyCodes } from "../../../utils/key_codes.jsx";
import { useFileIntegrations } from "../../Hooks/useFileIntegrations.jsx";

const Target = forwardRef(({ open, ...rest }, ref) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t("Attach Files")}>
      <IconButton
        variant="text gray"
        size="small"
        ref={ref}
        active={open}
        {...rest}
      >
        <AttachmentIcon className={classnames({ "tw-opacity-50": !open })} />
      </IconButton>
    </Tooltip>
  );
});

Target.propTypes = {
  open: PropTypes.bool,
};

Target.displayName = "Target";

export const AttachmentsPicker = ({ openFileWindow, onAttachFromDropbox, onAttachFromGoogleDrive, target }) => {
  const { hasAttachmentIntegration } = useFileIntegrations();

  const handleKeyDown = useCallback(
    (event) => {
      // when focused we need to open file input on enter,
      if (event.key === keyCodes.enter) {
        typeof openFileWindow === "function" && openFileWindow();
      }
    },
    [openFileWindow]
  );

  if (hasAttachmentIntegration) {
    return (
      <AttachmentMenuPicker
        openFileWindow={openFileWindow}
        onAttachFromDropbox={onAttachFromDropbox}
        onAttachFromGoogleDrive={onAttachFromGoogleDrive}
        target={target ? target : <Target />}
      />
    );
  }

  return target ? (
    React.cloneElement(target, {
      onClick: openFileWindow,
      onKeyDown: handleKeyDown,
    })
  ) : (
    <Target
      onKeyDown={handleKeyDown}
      onClick={openFileWindow}
    />
  );
};

AttachmentsPicker.propTypes = {
  openFileWindow: PropTypes.func,
  onAttachFromDropbox: PropTypes.func,
  onAttachFromGoogleDrive: PropTypes.func,
  target: PropTypes.node,
};
